import { StyledModalDialog } from "@src/common/styled-components";
import { LAYOUT_CONSTANTS } from "@src/constants";
import { CARE_FORMAT } from "@src/constants/filter-constants";
import type { AppointmentDetails } from "@src/context/ApplicationContext";
import type { Provider, ProviderDepartment } from "@src/interfaces/provider.interface";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Text } from "@uhg-abyss/web/ui/Text";

const ModalFooterContainer = styled("div", {
  display: "flex",
  justifyContent: "flex-end",
  width: "100%",
  paddingTop: "16px",
});

const AppointmentContentLine = styled("div", {
  display: "flex",
  width: "100%",
  gap: "8px",
});

interface PatientFoundNavigatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOk: () => void;
  selectedProvider?: Partial<Provider>;
  appointmentDetails?: Omit<Partial<AppointmentDetails>, "apptLocation"> & {
    apptLocation?: Partial<ProviderDepartment>;
  };
  patientFirstName?: string;
  patientLastName?: string;
}

export const PatientFoundNavigatorModal = ({
  isOpen,
  onClose,
  onOk,
  selectedProvider,
  appointmentDetails,
  patientFirstName,
  patientLastName,
}: PatientFoundNavigatorModalProps) => {
  const patientName = [patientFirstName?.trim(), patientLastName?.trim()].filter(Boolean).join(" ");

  const locationText = (() => {
    if (!appointmentDetails?.careFormat) return "N/A";
    if (appointmentDetails.careFormat === CARE_FORMAT.TELEHEALTH) return "Virtual Session";
    if (appointmentDetails.careFormat !== CARE_FORMAT.IN_PERSON) return "N/A";

    const loc = appointmentDetails.apptLocation;
    if (!loc) return "N/A";

    const parts = [
      loc.address_line_1,
      loc.address_line_2,
      loc.city,
      [loc.state, loc.zip_code].filter(Boolean).join(" "),
    ].filter(Boolean);

    return parts.length ? parts.join(", ") : "N/A";
  })();

  return (
    <StyledModalDialog
      data-testid="test-id-patient-found-patient-navigator-modal"
      header="Patient already exists"
      isOpen={isOpen}
      onClose={onClose}
      hideClose={true}
      closeOnClickOutside={false}
      size={LAYOUT_CONSTANTS.modalDialogWidth}
      footer={
        <ModalFooterContainer>
          <Button
            data-testid="test-id-patient-found-navigator-modal-ok-button"
            id="obc-patient-found-patient-navigator-modal-ok-btn"
            data-obc-label="Patient Found Patient Navigator Modal OK Button"
            onClick={onOk}
          >
            OK
          </Button>
        </ModalFooterContainer>
      }
    >
      <Text data-testid="test-id-patient-found-modal-content-subcontent1">
        A patient record matching this information already exists. Please schedule an appointment
        for this patient in Athena with the following details if you wish to proceed:
      </Text>
      <AppointmentContentLine>
        <Text
          data-testid="test-id-patient-found-modal-content-patient-name-label"
          fontWeight="bold"
        >
          Patient Name:
        </Text>
        <Text data-testid="test-id-patient-found-modal-content-patient-name-value">
          {patientName || "N/A"}
        </Text>
      </AppointmentContentLine>

      <AppointmentContentLine>
        <Text data-testid="test-id-patient-found-modal-content-context-id-label" fontWeight="bold">
          Context ID:
        </Text>
        <Text data-testid="test-id-patient-found-modal-content-context-id-value">
          {selectedProvider?.contextId ?? "N/A"}
        </Text>
      </AppointmentContentLine>
      <AppointmentContentLine>
        <Text
          data-testid="test-id-patient-found-modal-content-provider-name-label"
          fontWeight="bold"
        >
          Provider Name:
        </Text>
        <Text data-testid="test-id-patient-found-modal-content-provider-name-value">
          {selectedProvider?.fullName || "N/A"}
        </Text>
      </AppointmentContentLine>
      <AppointmentContentLine>
        <Text
          data-testid="test-id-patient-found-modal-content-appointment-date-time-label"
          fontWeight="bold"
        >
          Appointment Date/Time:
        </Text>
        <Text data-testid="test-id-patient-found-modal-content-appointment-date-time-value">
          {appointmentDetails?.apptDate && appointmentDetails.apptTime
            ? `${appointmentDetails.apptDate}, ${appointmentDetails.apptTime}`
            : "N/A"}
        </Text>
      </AppointmentContentLine>
      <AppointmentContentLine>
        <Text data-testid="test-id-patient-found-modal-content-location-label" fontWeight="bold">
          Location:
        </Text>
        <Text data-testid="test-id-patient-found-modal-content-location-value">{locationText}</Text>
      </AppointmentContentLine>
      <Text data-testid="test-id-patient-found-modal-content-subcontent2">
        Click <strong>OK</strong> once you have recorded this information for scheduling in Athena.
      </Text>
    </StyledModalDialog>
  );
};
